// src/features/admin/AdminPhotosPanel.jsx
//
// ADMIN-PHOTO-LIBRARY-S1: Photo Library panel for Admin → App & branding.
//
// Features:
//   - Thumbnail grid of all photos in infocus_photos + upload button.
//   - Per-tile: category select, region select, active toggle, delete button.
//
// Gated: rendered only when currentRole === 'admin' in AdminPage.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { ToastBanner } from "../../components/ToastBanner.jsx";

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = ["Decision", "Issue", "Project", "Action", "Watch", "Insight"];
const REGIONS    = ["AMER", "EMEA", "APAC"];
const API_PHOTOS = "/api/admin/photos";

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

// ─── PhotoTile ────────────────────────────────────────────────────────────────

function PhotoTile({ photo, onUpdate, onDelete }) {
  const [category,       setCategory]       = useState(photo.category);
  const [region,         setRegion]         = useState(photo.region ?? "");
  const [active,         setActive]         = useState(photo.active);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState(null);
  const [deleting,       setDeleting]       = useState(false);
  const [catFocused,     setCatFocused]     = useState(false);
  const [regionFocused,  setRegionFocused]  = useState(false);

  async function save(patch) {
    setSaving(true);
    setError(null);
    try {
      const updated = await apiFetch(`${API_PHOTOS}/${photo.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await apiFetch(`${API_PHOTOS}/${photo.id}`, { method: "DELETE" });
      onDelete(photo.id);
    } catch (e) {
      setError(e.message);
      setDeleting(false);
    }
  }

  return (
    <div
      data-testid={`photo-tile-${photo.id}`}
      style={{
        border: `0.5px solid ${T.border}`,
        borderRadius: 8, overflow: "hidden",
        background: T.white,
        opacity: (!active || deleting) ? 0.55 : 1,
        transition: "opacity .2s",
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: "100%", paddingTop: "56.25%", position: "relative",
        background: T.bg,
      }}>
        <img
          src={photo.url}
          alt={`${photo.category} photo`}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>

      {/* Controls */}
      <div style={{ padding: "10px 10px 8px", display: "flex", flexDirection: "column", gap: 6 }}>

        {/* Category */}
        <select
          data-testid={`category-select-${photo.id}`}
          value={category}
          disabled={saving}
          onChange={(e) => {
            setCategory(e.target.value);
            save({ category: e.target.value });
          }}
          onFocus={() => setCatFocused(true)}
          onBlur={() => setCatFocused(false)}
          style={{
            width: "100%",
            fontFamily: "inherit",
            fontSize: T.fsSm,
            padding: "5px 8px",
            border: `1px solid ${T.border}`,
            borderRadius: 4,
            background: T.white,
            color: T.dark,
            outline: catFocused ? `2px solid ${T.blue}` : "none",
            outlineOffset: 1,
          }}
        >
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Region */}
        <select
          data-testid={`region-select-${photo.id}`}
          value={region}
          disabled={saving}
          onChange={(e) => {
            setRegion(e.target.value);
            save({ region: e.target.value });
          }}
          onFocus={() => setRegionFocused(true)}
          onBlur={() => setRegionFocused(false)}
          style={{
            width: "100%",
            fontFamily: "inherit",
            fontSize: T.fsSm,
            padding: "5px 8px",
            border: `1px solid ${T.border}`,
            borderRadius: 4,
            background: T.white,
            color: T.dark,
            outline: regionFocused ? `2px solid ${T.blue}` : "none",
            outlineOffset: 1,
          }}
        >
          <option value="">No region</option>
          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>

        {/* Active + delete row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{
            display: "flex", alignItems: "center", gap: 5,
            fontSize: T.fsXs, color: T.mid, cursor: "pointer", flex: 1,
          }}>
            <input
              data-testid={`active-toggle-${photo.id}`}
              type="checkbox"
              checked={active}
              disabled={saving}
              onChange={(e) => {
                setActive(e.target.checked);
                save({ active: e.target.checked });
              }}
            />
            Active
          </label>

          <button
            data-testid={`delete-btn-${photo.id}`}
            onClick={handleDelete}
            disabled={deleting || saving}
            title="Delete photo"
            style={{
              border: `0.5px solid ${T.border}`,
              background: T.white, color: T.red,
              borderRadius: 4, padding: "2px 8px",
              fontSize: T.fsXs, cursor: "pointer",
            }}
          >
            Delete
          </button>
        </div>

        {error && (
          <div style={{ fontSize: T.fsXxs, color: T.red, marginTop: 2 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AdminPhotosPanel ─────────────────────────────────────────────────────────

export function AdminPhotosPanel() {
  const [photos,       setPhotos]      = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState(null);
  const [uploading,    setUploading]   = useState(false);
  const [uploadErr,    setUploadErr]   = useState(null);
  const [uploadHover,  setUploadHover] = useState(false);
  const [uploadToast,  setUploadToast] = useState(null);
  const [urlInput,     setUrlInput]    = useState("");
  const [urlCategory,  setUrlCategory] = useState("Decision");
  const [urlSubmitting, setUrlSubmitting] = useState(false);
  const [urlErr,       setUrlErr]      = useState(null);
  const fileInputRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(API_PHOTOS);
      setPhotos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  function handleUpdate(updated) {
    setPhotos((prev) => prev.map((p) => p.id === updated.id ? updated : p));
  }

  function handleDelete(id) {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  }

  async function handleUrlSubmit(e) {
    e.preventDefault();
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setUrlErr("URL is required");
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setUrlErr("URL must start with http:// or https://");
      return;
    }
    setUrlSubmitting(true);
    setUrlErr(null);
    setUploadToast(null);
    try {
      const newPhoto = await apiFetch(`${API_PHOTOS}/url`, {
        method: "POST",
        body: JSON.stringify({ url: trimmed, category: urlCategory, region: "" }),
      });
      setPhotos((prev) => [...prev, newPhoto]);
      setUploadToast("Photo added");
      setUrlInput("");
    } catch (err) {
      setUrlErr(err.message);
    } finally {
      setUrlSubmitting(false);
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadErr(null);
    setUploadToast(null);

    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("category", "Decision"); // default; user can change on the tile
      fd.append("region", "");

      const res = await fetch(API_PHOTOS + "/upload", { method: "POST", body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Upload failed: ${res.status}`);
      }
      const newPhoto = await res.json();
      setPhotos((prev) => [...prev, newPhoto]);
      setUploadToast("Photo uploaded");
    } catch (e) {
      setUploadErr(e.message);
    } finally {
      setUploading(false);
      // Reset input so the same file can be re-picked.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <div data-testid="admin-photos-panel">
      {/* Section heading */}
      <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
        App &amp; branding
      </div>
      <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 18, lineHeight: 1.5 }}>
        Manage the In Focus photo library.
      </div>

      <>
        {/* Upload controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <button
              data-testid="upload-photos-btn"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              onMouseEnter={() => setUploadHover(true)}
              onMouseLeave={() => setUploadHover(false)}
              style={{
                padding: "7px 16px",
                background: uploading ? T.bg : T.white,
                color: uploading ? T.mid : T.dark,
                border: uploading
                  ? `1px solid ${T.border}`
                  : `1px solid ${uploadHover ? T.accent : T.border}`,
                borderRadius: 6,
                fontSize: T.fsSm,
                fontWeight: T.fwSemibold,
                cursor: uploading ? "not-allowed" : "pointer",
                opacity: uploading ? 0.5 : 1,
                fontFamily: "inherit",
              }}
            >
              {uploading ? "Uploading…" : "Upload photos"}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {uploadErr && (
              <span style={{ fontSize: T.fsXs, color: T.red }}>{uploadErr}</span>
            )}
          </div>

          {/* Add by URL */}
          <form
            data-testid="add-by-url-form"
            onSubmit={handleUrlSubmit}
            style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}
          >
            <input
              data-testid="url-input"
              type="url"
              placeholder="https://example.com/photo.jpg"
              value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setUrlErr(null); }}
              disabled={urlSubmitting}
              style={{
                flex: "1 1 220px", minWidth: 180,
                fontFamily: "inherit",
                fontSize: T.fsSm,
                padding: "6px 10px",
                border: `1px solid ${urlErr ? T.red : T.border}`,
                borderRadius: 6,
                background: T.white,
                color: T.dark,
                outline: "none",
              }}
            />
            <select
              data-testid="url-category-select"
              value={urlCategory}
              onChange={(e) => setUrlCategory(e.target.value)}
              disabled={urlSubmitting}
              style={{
                fontFamily: "inherit",
                fontSize: T.fsSm,
                padding: "6px 8px",
                border: `1px solid ${T.border}`,
                borderRadius: 6,
                background: T.white,
                color: T.dark,
              }}
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              data-testid="add-by-url-btn"
              type="submit"
              disabled={urlSubmitting}
              style={{
                padding: "6px 14px",
                background: T.white,
                color: T.dark,
                border: `1px solid ${T.border}`,
                borderRadius: 6,
                fontSize: T.fsSm,
                fontWeight: T.fwSemibold,
                cursor: urlSubmitting ? "not-allowed" : "pointer",
                opacity: urlSubmitting ? 0.5 : 1,
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              {urlSubmitting ? "Adding…" : "Add by URL"}
            </button>
            {urlErr && (
              <span style={{ width: "100%", fontSize: T.fsXs, color: T.red }}>{urlErr}</span>
            )}
          </form>

          {/* Error state */}
          {error && <ErrorBanner error={error} />}

          {/* Loading state */}
          {loading && (
            <div style={{ color: T.mid, fontSize: T.fsSm, padding: "24px 0" }}>
              Loading photo library…
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && photos.length === 0 && (
            <div style={{
              padding: "48px 24px", textAlign: "center",
              color: T.light, fontSize: T.fsBase,
              background: T.bg, border: `0.5px solid ${T.border}`, borderRadius: 6,
            }}>
              No photos yet. Upload your first photo above.
            </div>
          )}

          {/* Thumbnail grid */}
          {!loading && photos.length > 0 && (
            <div
              data-testid="photos-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: 14,
              }}
            >
              {photos.map((photo) => (
                <PhotoTile
                  key={photo.id}
                  photo={photo}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
      </>

      {uploadToast && (
        <ToastBanner message={uploadToast} type="success" onDismiss={() => setUploadToast(null)} />
      )}
    </div>
  );
}
